import {
    Color3,
    Color4,
    Constants,
    Mesh,
    MeshBuilder,
    ParticleSystem,
    Quaternion,
    StandardMaterial,
    TransformNode,
    Vector3,
    VertexData,
    type Scene,
    type ShaderMaterial,
} from '@babylonjs/core';
import { BOLT_OF_WRATH_GATHER_MS } from '@sorrem/shared';
import { getGlowHaloTexture } from '../procedural-textures';
import { applyParticleSprite, CRACKLE_LOOK, createSpriteMaterial, getParticleSprite, LIGHTNING_RUN_SPRITES, SPARK_LOOK } from './particle-sprites';
import type { VfxWarmup } from './vfx-warmup';
import { registerWrathBoltShaders } from './wrath-bolt-shaders';
import { createVfxShaderMaterial } from './vfx-shader-material';

/** How a bolt ended: it struck its target, or it missed and burned out at the end of its reach. */
export type WrathBoltOutcome = 'hit' | 'miss';

/** How a bolt is drawn: the flame lance, or a short flying bolt of lightning when it lances through enemies (Fury's Edge). */
export type WrathBoltKind = 'flame' | 'lightning';

/** One bolt this frame, as its projectile is flown. */
export interface WrathBoltState {
    readonly id: string;
    /**
     * Where the flame is, in scene space: in the casting hand while it
     * gathers (the cast clip may still be raising it), then the lance's nose
     * as it flies.
     */
    readonly position: Vector3;
    /** Still gathering in the hand: the lance has not left it. */
    readonly isGathering: boolean;
    /** How it is drawn; the flame lance when left out. Read on its first frame. */
    readonly kind?: WrathBoltKind;
}

/** How a bolt ended, where, and which way it was going. */
export interface WrathBoltEnding {
    readonly id: string;
    readonly outcome: WrathBoltOutcome;
    /** Where it ended, in scene space: on the target for a hit, at the end of its reach for a miss. */
    readonly point: Vector3;
    /** Which way it was flying, in scene space: a hit's sparks spray on along it. */
    readonly direction: Vector3;
    /** How it is drawn (`WrathBoltState.kind`), for one whose ending plays alone; the flame lance when left out. */
    readonly kind?: WrathBoltKind;
}

/** The flame swells in the hand over the bolt's gather, from a third of its size. */
const GATHER_SECONDS = BOLT_OF_WRATH_GATHER_MS / 1000;
/** The hand flame gutters out this long after the lance leaves. */
const HAND_FADE_SECONDS = 0.12;
/** How long a hit's flare and a miss's sputter play. */
const FINISH_SECONDS = 0.3;
/** How long after the finish the bolt lingers so its embers and sparks can die out. */
const LINGER_SECONDS = 0.6;

const LANCE_LENGTH = 1.1;
const LANCE_WIDTH = 0.28;
const HAND_FLARE_SIZE = 0.5;
const IMPACT_FLARE_SIZE = 1.1;

/** Lightning (Fury's Edge) takes a new jagged shape this often, which is its flicker. */
const LIGHTNING_FLICKER_SECONDS = 0.045;
/** How long the flying bolt of lightning is, trailing back from its head (shorter as it first leaves the hand). */
const LIGHTNING_LENGTH = 1.4;
/** How wide each tall crackle sprite is drawn, about how long a run of it is, and how far a kink strays from its line. */
const LIGHTNING_WIDTH = 0.5;
const LIGHTNING_SEGMENT_LENGTH = 0.45;
const LIGHTNING_JITTER = 0.15;
/** Fury's white-hot lightning: the gray crackle sprites tinted toward a warm white. */
const LIGHTNING_COLOR = new Color3(1, 0.78, 0.62);
/** Short forks off the bolt each flicker: how many at most, and how long. */
const LIGHTNING_FORKS = 2;
const LIGHTNING_FORK_LENGTH = 0.4;

/**
 * What sets a flame bolt and a lightning one apart, chosen once per bolt:
 * the lance's crossed quads, its own flickering bolt, and how its trail,
 * a hit's sparks and a miss's sputter differ from the flame's embers.
 */
interface WrathBoltLook {
    /** The lance's quads, rolled about its flight so it reads from any angle; none for lightning. */
    readonly lanceRolls: readonly number[];
    readonly hasLightning: boolean;
    /** Restyles the flame's ember trail. */
    readonly styleTrail: (trail: ParticleSystem, scene: Scene) => void;
    /** Styles a hit's sparks: their sprite and colors, and any other changes. */
    readonly styleSparks: (sparks: ParticleSystem, scene: Scene) => void;
    /** Restyles a miss's ember sputter. */
    readonly styleSputter: (sputter: ParticleSystem, scene: Scene) => void;
}

const WRATH_BOLT_LOOKS: Record<WrathBoltKind, WrathBoltLook> = {
    flame: {
        lanceRolls: [0, Math.PI / 2],
        hasLightning: false,
        styleTrail: (): void => undefined,
        styleSparks: (sparks, scene): void => {
            applyParticleSprite(sparks, scene, SPARK_LOOK);
            sparks.color1 = new Color4(1, 0.95, 0.7, 1);
            sparks.color2 = new Color4(1, 0.55, 0.12, 1);
        },
        styleSputter: (): void => undefined,
    },
    lightning: {
        lanceRolls: [],
        hasLightning: true,
        // Fewer, white-hot crackles.
        styleTrail: (trail, scene): void => {
            applyParticleSprite(trail, scene, CRACKLE_LOOK);
            trail.emitRate = 60;
            trail.minLifeTime = 0.08;
            trail.maxLifeTime = 0.18;
            trail.minSize = 0.15;
            trail.maxSize = 0.3;
            trail.minEmitPower = 0.5;
            trail.maxEmitPower = 1.5;
            trail.gravity = Vector3.Zero();
            trail.color1 = new Color4(1, 0.97, 0.9, 1);
            trail.color2 = new Color4(1, 0.45, 0.2, 1);
        },
        // Fewer, larger crackles, gone in a blink.
        styleSparks: (sparks, scene): void => {
            applyParticleSprite(sparks, scene, CRACKLE_LOOK);
            sparks.color1 = new Color4(1, 1, 0.97, 1);
            sparks.color2 = new Color4(1, 0.5, 0.3, 1);
            sparks.manualEmitCount = 16;
            sparks.minSize = 0.2;
            sparks.maxSize = 0.45;
            sparks.minLifeTime = 0.1;
            sparks.maxLifeTime = 0.25;
            sparks.gravity = Vector3.Zero();
        },
        // It fizzles out in a few crackles.
        styleSputter: (sputter, scene): void => {
            applyParticleSprite(sputter, scene, CRACKLE_LOOK);
            sputter.manualEmitCount = 8;
            sputter.minSize = 0.15;
            sputter.maxSize = 0.3;
            sputter.minLifeTime = 0.08;
            sputter.maxLifeTime = 0.2;
            sputter.gravity = Vector3.Zero();
            sputter.color1 = new Color4(1, 0.97, 0.9, 1);
            sputter.color2 = new Color4(1, 0.45, 0.2, 1);
        },
    },
};

interface DrawnLightning {
    readonly mesh: Mesh;
    /** A tall crackle sprite (`LIGHTNING_RUN_SPRITES`), swapped each flicker. */
    readonly material: StandardMaterial;
    sinceRebuildSeconds: number;
}

interface DrawnBolt {
    readonly id: string;
    readonly look: WrathBoltLook;
    readonly root: TransformNode;
    readonly head: TransformNode;
    readonly lanceMaterial: ShaderMaterial;
    readonly handFlare: Mesh;
    readonly handFlareMaterial: ShaderMaterial;
    readonly impactFlare: Mesh;
    readonly impactFlareMaterial: ShaderMaterial;
    readonly trail: ParticleSystem;
    readonly trailEmitter: Vector3;
    /** Its lightning, when it lances (Fury's Edge); null for the flame lance. */
    readonly lightning: DrawnLightning | null;
    /** Where the lance left the hand; set once it does. */
    launchedFrom: Vector3 | null;
    /** How long it has gathered, and how long since the lance left. */
    gatherSeconds: number;
    sinceLaunchSeconds: number;
    /** How it ended, once it has: a hit's spray or a miss's sputter; null for one taken away. */
    ending: { readonly outcome: WrathBoltOutcome | null; readonly burst: ParticleSystem | null; seconds: number } | null;
}

/**
 * Draws Bolt of Wrath (docs/gameplay/combat-vfx.md) as its projectile is
 * flown: a fury flame gathers in the casting hand, a white-cored flame lance
 * with an ember trail flies where the projectile is, and how it ended plays
 * where it ended: a hit flares on the target with sparks spraying on
 * through it, and a miss sputters out at the end of its reach. One that
 * lances through enemies (Fury's Edge) is drawn as a short, flickering bolt
 * of lightning flying where the projectile is, instead of the lance.
 *
 * The host rolls the hit as the bolt arrives, so the flinch and damage
 * number land with it. Embers and sparks are Babylon particles - client
 * visuals need no `SeededRng`.
 */
export class WrathBoltSystem {
    private readonly bolts = new Map<string, DrawnBolt>();
    private timeSeconds = 0;

    constructor(private readonly scene: Scene) {
        registerWrathBoltShaders();
    }

    /** Readies this system's shaders and textures before its first effect, so it shows at once (`VfxWarmup`). */
    warm(warmup: VfxWarmup): void {
        warmup.material(this.createMaterial('wrath-bolt-warmup-lance', 'wrathLance', ['time', 'fade']));
        warmup.material(this.createMaterial('wrath-bolt-warmup-flare', 'wrathFlare', ['time', 'intensity', 'seed']));
        warmup.material(createSpriteMaterial(this.scene, 'wrath-bolt-warmup-lightning', LIGHTNING_RUN_SPRITES[0]!, LIGHTNING_COLOR, true));
        const { flame, lightning } = WRATH_BOLT_LOOKS;
        warmup.particleSystem(this.createTrail('wrath-bolt-warmup-crackle-trail', Vector3.Zero(), lightning));
        warmup.particleSystem(this.createSparks('wrath-bolt-warmup-crackles', Vector3.Zero(), Vector3.Right(), lightning));
        warmup.particleSystem(this.createTrail('wrath-bolt-warmup-trail', Vector3.Zero(), flame));
        warmup.particleSystem(this.createSparks('wrath-bolt-warmup-sparks', Vector3.Zero(), Vector3.Right(), flame));
        warmup.particleSystem(this.createSputter('wrath-bolt-warmup-sputter', Vector3.Zero(), flame));
        warmup.particleSystem(this.createSputter('wrath-bolt-warmup-fizzle', Vector3.Zero(), lightning));
    }

    /**
     * Shows exactly these bolts where they are this frame. One no longer
     * here that has not ended was taken away (the run ended): it simply
     * goes out.
     */
    setBolts(states: readonly WrathBoltState[]): void {
        const seen = new Set<string>();
        for (const state of states) {
            seen.add(state.id);
            const bolt = this.bolts.get(state.id) ?? this.createBolt(state.id, state.position, WRATH_BOLT_LOOKS[state.kind ?? 'flame']);
            if (bolt.ending) {
                continue;
            }
            if (state.isGathering && !bolt.launchedFrom) {
                bolt.handFlare.position.copyFrom(state.position);
            } else {
                this.fly(bolt, state.position);
            }
        }
        for (const bolt of this.bolts.values()) {
            if (!seen.has(bolt.id) && !bolt.ending) {
                this.finish(bolt, null, bolt.head.position, Vector3.Right());
            }
        }
    }

    /** Plays how a bolt ended where it ended; one never drawn (or already gone) plays its ending alone. */
    end(ending: WrathBoltEnding): void {
        const existing = this.bolts.get(ending.id);
        if (existing?.ending && existing.ending.outcome !== null) {
            return;
        }
        const bolt = existing ?? this.createBolt(ending.id, ending.point, WRATH_BOLT_LOOKS[ending.kind ?? 'flame']);
        if (bolt.ending) {
            // Taken away a moment before its ending arrived: start it again from its ending.
            bolt.ending.burst?.dispose(false);
            bolt.ending = null;
        }
        if (!bolt.launchedFrom) {
            bolt.launchedFrom = ending.point.clone();
            bolt.gatherSeconds = GATHER_SECONDS;
            bolt.sinceLaunchSeconds = HAND_FADE_SECONDS;
        }
        this.finish(bolt, ending.outcome, ending.point, ending.direction);
    }

    update(deltaSeconds: number): void {
        this.timeSeconds += deltaSeconds;
        for (const bolt of [...this.bolts.values()]) {
            if (bolt.ending) {
                bolt.ending.seconds += deltaSeconds;
                if (bolt.ending.seconds >= FINISH_SECONDS + LINGER_SECONDS) {
                    this.disposeBolt(bolt);
                    this.bolts.delete(bolt.id);
                    continue;
                }
            }
            if (bolt.launchedFrom) {
                bolt.sinceLaunchSeconds += deltaSeconds;
            } else {
                bolt.gatherSeconds += deltaSeconds;
            }
            if (bolt.lightning) {
                bolt.lightning.sinceRebuildSeconds += deltaSeconds;
            }
            this.applyFrame(bolt);
        }
    }

    dispose(): void {
        this.bolts.forEach((bolt) => this.disposeBolt(bolt));
        this.bolts.clear();
    }

    private createBolt(id: string, position: Vector3, look: WrathBoltLook): DrawnBolt {
        const name = `wrath-bolt-${id}`;
        const root = new TransformNode(name, this.scene);
        const head = new TransformNode(`${name}-head`, this.scene);
        head.parent = root;
        head.position.copyFrom(position);
        head.rotationQuaternion = new Quaternion();
        head.setEnabled(false);

        const lanceMaterial = this.createMaterial(`${name}-lance`, 'wrathLance', ['time', 'fade']);
        for (const roll of look.lanceRolls) {
            const quad = MeshBuilder.CreatePlane(`${name}-lance-${roll === 0 ? 'a' : 'b'}`, { width: LANCE_LENGTH, height: LANCE_WIDTH, sideOrientation: Mesh.DOUBLESIDE }, this.scene);
            quad.parent = head;
            // The nose sits on the head node; the quad trails behind it.
            quad.position.x = -LANCE_LENGTH / 2;
            quad.rotation.x = roll;
            quad.material = lanceMaterial;
            quad.isPickable = false;
        }

        const handFlareMaterial = this.createMaterial(`${name}-hand-flare`, 'wrathFlare', ['time', 'intensity', 'seed']);
        const handFlare = this.createFlare(`${name}-hand-flare`, root, position, HAND_FLARE_SIZE, handFlareMaterial);
        const impactFlareMaterial = this.createMaterial(`${name}-impact-flare`, 'wrathFlare', ['time', 'intensity', 'seed']);
        const impactFlare = this.createFlare(`${name}-impact-flare`, root, position, IMPACT_FLARE_SIZE, impactFlareMaterial);
        impactFlare.setEnabled(false);

        const seed = Math.random();
        handFlareMaterial.setFloat('seed', seed);
        impactFlareMaterial.setFloat('seed', seed + 0.37);

        const trailEmitter = position.clone();
        const bolt: DrawnBolt = {
            lightning: look.hasLightning ? this.createLightning(name, root) : null,
            id,
            look,
            root,
            head,
            lanceMaterial,
            handFlare,
            handFlareMaterial,
            impactFlare,
            impactFlareMaterial,
            trail: this.createTrail(`${name}-trail`, trailEmitter, look),
            trailEmitter,
            launchedFrom: null,
            gatherSeconds: 0,
            sinceLaunchSeconds: 0,
            ending: null,
        };
        this.bolts.set(id, bolt);
        this.applyFrame(bolt);
        return bolt;
    }

    /** The lance's nose is at `position` this frame: it leaves the hand the first time, and points the way it moved. */
    private fly(bolt: DrawnBolt, position: Vector3): void {
        if (!bolt.launchedFrom) {
            bolt.launchedFrom = bolt.handFlare.position.clone();
            bolt.head.position.copyFrom(bolt.launchedFrom);
        }
        const moved = position.subtract(bolt.head.position);
        if (moved.lengthSquared() > 1e-8) {
            Quaternion.FromUnitVectorsToRef(Vector3.Right(), moved.normalize(), bolt.head.rotationQuaternion!);
        }
        bolt.head.position.copyFrom(position);
        bolt.trailEmitter.copyFrom(position);
        if (!bolt.trail.isStarted()) {
            bolt.trail.start();
        }
    }

    /** It ended: the trail stops, and a hit's sparks or a miss's sputter burst where it ended; one taken away just goes out. */
    private finish(bolt: DrawnBolt, outcome: WrathBoltOutcome | null, point: Vector3, direction: Vector3): void {
        bolt.head.position.copyFrom(point);
        bolt.impactFlare.position.copyFrom(point);
        bolt.trail.stop();
        const burst = this.createEndingBurst(bolt, outcome, point, direction);
        burst?.start();
        bolt.ending = { outcome, burst, seconds: 0 };
        this.applyFrame(bolt);
    }

    /** A hit's sparks or a miss's sputter; nothing for one taken away. */
    private createEndingBurst(bolt: DrawnBolt, outcome: WrathBoltOutcome | null, point: Vector3, direction: Vector3): ParticleSystem | null {
        switch (outcome) {
            case 'hit':
                return this.createSparks(`wrath-bolt-${bolt.id}-sparks`, point, direction.normalizeToNew(), bolt.look);
            case 'miss':
                return this.createSputter(`wrath-bolt-${bolt.id}-sputter`, point, bolt.look);
            case null:
                return null;
            default: {
                const unhandled: never = outcome;
                throw new Error(`Unhandled wrath bolt outcome: ${String(unhandled)}`);
            }
        }
    }

    private applyFrame(bolt: DrawnBolt): void {
        // The hand flame swells through the gather, then gutters out as the lance leaves.
        const gather = Math.min(1, bolt.gatherSeconds / GATHER_SECONDS);
        const handFade = bolt.launchedFrom ? Math.max(0, 1 - (bolt.sinceLaunchSeconds / HAND_FADE_SECONDS)) : 1;
        bolt.handFlare.setEnabled(handFade > 0);
        bolt.handFlare.scaling.setAll(0.35 + (0.65 * gather));
        bolt.handFlareMaterial.setFloat('intensity', 1.3 * gather * handFade);
        bolt.handFlareMaterial.setFloat('time', this.timeSeconds);

        this.applyLightningFrame(bolt);
        const { ending } = bolt;
        if (!ending) {
            if (bolt.launchedFrom) {
                // Grow out of the hand rather than reaching back behind it.
                const travelled = Vector3.Distance(bolt.launchedFrom, bolt.head.position);
                bolt.head.scaling.x = Math.min(1, Math.max(0.15, travelled / LANCE_LENGTH));
                bolt.head.setEnabled(true);
                bolt.lanceMaterial.setFloat('fade', 1);
            }
        } else {
            const finishProgress = Math.min(1, ending.seconds / FINISH_SECONDS);
            if (ending.outcome === 'hit') {
                bolt.head.setEnabled(false);
                bolt.impactFlare.setEnabled(finishProgress < 1);
                bolt.impactFlare.scaling.setAll(0.6 + (0.6 * finishProgress));
                bolt.impactFlareMaterial.setFloat('intensity', 1.6 * (1 - finishProgress));
            } else {
                // A miss holds at the end of its reach and burns down; one taken away just goes out.
                bolt.head.scaling.x = Math.max(0.05, 1 - finishProgress);
                bolt.lanceMaterial.setFloat('fade', 1 - finishProgress);
                bolt.head.setEnabled(bolt.launchedFrom !== null && finishProgress < 1);
            }
        }
        bolt.lanceMaterial.setFloat('time', this.timeSeconds);
        bolt.impactFlareMaterial.setFloat('time', this.timeSeconds);
    }

    /** Lightning this frame: a short bolt behind the head, a new jagged shape each flicker, fading out once it has ended. */
    private applyLightningFrame(bolt: DrawnBolt): void {
        const { lightning, launchedFrom, ending } = bolt;
        if (!lightning) {
            return;
        }
        const fade = ending ? Math.max(0, 1 - (ending.seconds / FINISH_SECONDS)) : 1;
        lightning.mesh.setEnabled(launchedFrom !== null && fade > 0);
        if (!launchedFrom || lightning.sinceRebuildSeconds < LIGHTNING_FLICKER_SECONDS) {
            lightning.material.alpha = Math.min(lightning.material.alpha, fade);
            return;
        }
        lightning.sinceRebuildSeconds = 0;
        // Each flicker: another of the tall crackles, at another brightness.
        const sprite = LIGHTNING_RUN_SPRITES[Math.floor(Math.random() * LIGHTNING_RUN_SPRITES.length)] ?? LIGHTNING_RUN_SPRITES[0]!;
        const texture = getParticleSprite(this.scene, sprite);
        lightning.material.emissiveTexture = texture;
        lightning.material.opacityTexture = texture;
        lightning.material.alpha = (0.65 + (Math.random() * 0.35)) * fade;
        // It trails back from its head along its flight, never behind the hand it left.
        const head = bolt.head.position;
        const flown = Vector3.Distance(launchedFrom, head);
        const tail = flown > 1e-6 ? Vector3.Lerp(head, launchedFrom, Math.min(1, LIGHTNING_LENGTH / flown)) : head;
        this.buildLightning(lightning.mesh, tail, head);
    }

    /**
     * Lays a new jagged bolt from `from` to `to`, with short forks off it:
     * runs kinked aside at random, each a camera-facing quad with a tall
     * crackle sprite along it, flipped at random so no two runs match.
     */
    private buildLightning(mesh: Mesh, from: Vector3, to: Vector3): void {
        const length = Vector3.Distance(from, to);
        if (length < 0.05) {
            mesh.setEnabled(false);
            return;
        }
        const along = to.subtract(from).scaleInPlace(1 / length);
        const view = this.scene.activeCamera?.getDirection(Vector3.Forward()) ?? Vector3.Down();
        const aside = Vector3.Cross(along, view);
        if (aside.lengthSquared() < 1e-8) {
            aside.copyFrom(Vector3.Up());
        }
        aside.normalize();

        const runs = Math.max(3, Math.ceil(length / LIGHTNING_SEGMENT_LENGTH));
        const points: Vector3[] = [];
        for (let index = 0; index <= runs; index += 1) {
            // The ends stay put, on the hand and on the head.
            const kink = index === 0 || index === runs ? 0 : (Math.random() * 2 - 1) * LIGHTNING_JITTER;
            points.push(from.add(along.scale(length * (index / runs))).addInPlace(aside.scale(kink)));
        }

        const positions: number[] = [];
        const uvs: number[] = [];
        const indices: number[] = [];
        const addRun = (start: Vector3, end: Vector3, width: number): void => {
            const run = end.subtract(start);
            const side = Vector3.Cross(run, view);
            if (side.lengthSquared() < 1e-10) {
                return;
            }
            side.normalize().scaleInPlace(width / 2);
            const base = positions.length / 3;
            for (const corner of [start.subtract(side), start.add(side), end.add(side), end.subtract(side)]) {
                positions.push(corner.x, corner.y, corner.z);
            }
            // The sprite's height runs along the run.
            const flip = Math.random() < 0.5;
            uvs.push(flip ? 1 : 0, 0, flip ? 0 : 1, 0, flip ? 0 : 1, 1, flip ? 1 : 0, 1);
            indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
        };
        for (let index = 0; index < runs; index += 1) {
            addRun(points[index]!, points[index + 1]!, LIGHTNING_WIDTH);
        }
        const forks = Math.floor(Math.random() * (LIGHTNING_FORKS + 1));
        for (let fork = 0; fork < forks; fork += 1) {
            const start = points[1 + Math.floor(Math.random() * (runs - 1))]!;
            const bend = aside.scale((Math.random() < 0.5 ? -1 : 1) * (0.5 + Math.random() * 0.5));
            const end = start.add(along.add(bend).normalize().scaleInPlace(LIGHTNING_FORK_LENGTH * (0.5 + Math.random() * 0.5)));
            const middle = Vector3.Lerp(start, end, 0.5).addInPlace(aside.scale((Math.random() * 2 - 1) * LIGHTNING_JITTER * 0.5));
            addRun(start, middle, LIGHTNING_WIDTH * 0.55);
            addRun(middle, end, LIGHTNING_WIDTH * 0.4);
        }

        const vertexData = new VertexData();
        vertexData.positions = positions;
        vertexData.uvs = uvs;
        vertexData.indices = indices;
        vertexData.applyToMesh(mesh, false);
    }

    private createLightning(name: string, parent: TransformNode): DrawnLightning {
        const mesh = new Mesh(`${name}-lightning`, this.scene);
        mesh.parent = parent;
        mesh.isPickable = false;
        mesh.setEnabled(false);
        const material = createSpriteMaterial(this.scene, `${name}-lightning`, LIGHTNING_RUN_SPRITES[0]!, LIGHTNING_COLOR, true);
        mesh.material = material;
        // Its first flicker is built as soon as it leaves the hand.
        return { mesh, material, sinceRebuildSeconds: LIGHTNING_FLICKER_SECONDS };
    }

    private createMaterial(name: string, shaderName: string, uniforms: readonly string[]): ShaderMaterial {
        return createVfxShaderMaterial(this.scene, name, shaderName, { uniforms: ['worldViewProjection', ...uniforms], alphaMode: Constants.ALPHA_ADD });
    }

    private createFlare(name: string, parent: TransformNode, position: Vector3, size: number, material: ShaderMaterial): Mesh {
        const flare = MeshBuilder.CreatePlane(name, { size }, this.scene);
        flare.parent = parent;
        flare.position.copyFrom(position);
        flare.billboardMode = Mesh.BILLBOARDMODE_ALL;
        flare.material = material;
        flare.isPickable = false;
        return flare;
    }

    /** Embers shed behind the lance as it flies, drifting up as they cool; lightning sheds fewer, white-hot crackles. */
    private createTrail(name: string, emitter: Vector3, look: WrathBoltLook): ParticleSystem {
        const trail = new ParticleSystem(name, 200, this.scene);
        trail.particleTexture = getGlowHaloTexture(this.scene);
        trail.emitter = emitter;
        trail.emitRate = 260;
        trail.minLifeTime = 0.18;
        trail.maxLifeTime = 0.38;
        trail.minSize = 0.05;
        trail.maxSize = 0.13;
        trail.minEmitPower = 0.2;
        trail.maxEmitPower = 0.8;
        trail.direction1 = new Vector3(-0.6, -0.3, -0.6);
        trail.direction2 = new Vector3(0.6, 0.8, 0.6);
        trail.gravity = new Vector3(0, 1.2, 0);
        trail.color1 = new Color4(1, 0.78, 0.32, 1);
        trail.color2 = new Color4(1, 0.36, 0.08, 1);
        trail.colorDead = new Color4(0.35, 0.04, 0, 0);
        look.styleTrail(trail, this.scene);
        trail.blendMode = ParticleSystem.BLENDMODE_ADD;
        return trail;
    }

    /** A hit's sparks, sprayed on through the target and away from the caster; crackles of lightning for lightning. */
    private createSparks(name: string, point: Vector3, direction: Vector3, look: WrathBoltLook): ParticleSystem {
        const sparks = new ParticleSystem(name, 60, this.scene);
        sparks.emitter = point.clone();
        sparks.manualEmitCount = 45;
        sparks.minLifeTime = 0.2;
        sparks.maxLifeTime = 0.5;
        sparks.minSize = 0.04;
        sparks.maxSize = 0.1;
        sparks.minEmitPower = 3;
        sparks.maxEmitPower = 7;
        sparks.direction1 = direction.add(new Vector3(-0.7, -0.3, -0.7));
        sparks.direction2 = direction.add(new Vector3(0.7, 0.9, 0.7));
        sparks.gravity = new Vector3(0, -7, 0);
        look.styleSparks(sparks, this.scene);
        sparks.colorDead = new Color4(0.5, 0.08, 0, 0);
        sparks.blendMode = ParticleSystem.BLENDMODE_ADD;
        return sparks;
    }

    /** A miss burning out: a soft puff of embers that drifts up and dims; lightning fizzles out in a few crackles. */
    private createSputter(name: string, point: Vector3, look: WrathBoltLook): ParticleSystem {
        const sputter = new ParticleSystem(name, 40, this.scene);
        sputter.particleTexture = getGlowHaloTexture(this.scene);
        sputter.emitter = point.clone();
        sputter.manualEmitCount = 28;
        sputter.minLifeTime = 0.3;
        sputter.maxLifeTime = 0.65;
        sputter.minSize = 0.05;
        sputter.maxSize = 0.12;
        sputter.minEmitPower = 0.4;
        sputter.maxEmitPower = 1.4;
        sputter.direction1 = new Vector3(-1, -0.2, -1);
        sputter.direction2 = new Vector3(1, 1, 1);
        sputter.gravity = new Vector3(0, 1.4, 0);
        sputter.color1 = new Color4(1, 0.6, 0.18, 1);
        sputter.color2 = new Color4(0.9, 0.3, 0.06, 1);
        sputter.colorDead = new Color4(0.2, 0.02, 0, 0);
        look.styleSputter(sputter, this.scene);
        sputter.blendMode = ParticleSystem.BLENDMODE_ADD;
        return sputter;
    }

    private disposeBolt(bolt: DrawnBolt): void {
        // The particle texture is the shared glow halo, so it is kept.
        bolt.trail.dispose(false);
        bolt.ending?.burst?.dispose(false);
        bolt.lanceMaterial.dispose();
        bolt.lightning?.material.dispose();
        bolt.handFlareMaterial.dispose();
        bolt.impactFlareMaterial.dispose();
        bolt.root.dispose();
    }
}
